// src/utils/requestDeduplication.js - Enhanced Request deduplication utility

/**
 * Enhanced request deduplication utility to prevent duplicate API calls
 * Features:
 * - Smart similar request detection
 * - Improved cache management
 * - Better error handling
 * - Performance monitoring
 */
class EnhancedRequestDeduplicator {
  constructor() {
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.requestHistory = new Map(); // Track request patterns
    this.defaultTTL = 30000; // 30 seconds default cache TTL
    this.stats = {
      hits: 0,
      misses: 0,
      duplicatesPrevented: 0,
      similarRequestsPrevented: 0
    };
  }

  /**
   * Generate a normalized cache key from request parameters
   * @param {string} baseKey - Base identifier for the request
   * @param {Object} params - Request parameters
   * @returns {string} Normalized cache key
   */
  generateCacheKey(baseKey, params = {}) {
    const normalizedParams = this.normalizeParams(params);
    const sortedKeys = Object.keys(normalizedParams).sort();
    const paramString = sortedKeys
      .map(key => `${key}:${normalizedParams[key]}`)
      .join('|');

    const cacheKey = paramString ? `${baseKey}|${paramString}` : baseKey;

    return cacheKey;
  }

  /**
   * Normalize parameters to ensure consistent cache keys
   * @param {Object} params - Parameters to normalize
   * @returns {Object} Normalized parameters
   */
  normalizeParams(params) {
    const normalized = {};

    Object.keys(params).forEach(key => {
      const value = params[key];

      // Normalize numbers
      if (typeof value === 'number' || (typeof value === 'string' && !isNaN(value))) {
        normalized[key] = parseInt(value) || 0;
      }
      // Normalize strings
      else if (typeof value === 'string') {
        normalized[key] = value.trim().toLowerCase();
      }
      // Keep other types as-is
      else if (value !== null && value !== undefined) {
        normalized[key] = value;
      }
    });

    return normalized;
  }

  /**
   * Check if a request is similar to any pending request
   * @param {string} cacheKey - Current request cache key
   * @returns {Promise|null} Pending promise if similar request found
   */
  findSimilarPendingRequest(cacheKey) {
    const keyParts = cacheKey.split('|');
    const baseKey = keyParts[0];

    for (const [pendingKey, pendingPromise] of this.pendingRequests.entries()) {
      // Exact match
      if (pendingKey === cacheKey) {

        this.stats.duplicatesPrevented++;
        return pendingPromise;
      }

      // Similar request check
      if (this.areRequestsSimilar(cacheKey, pendingKey)) {

        this.stats.similarRequestsPrevented++;
        return pendingPromise;
      }
    }


    return null;
  }

  /**
   * Check if two requests are similar enough to deduplicate
   * @param {string} key1 - First request key
   * @param {string} key2 - Second request key
   * @returns {boolean} True if requests are similar
   */
  areRequestsSimilar(key1, key2) {
    const parts1 = key1.split('|');
    const parts2 = key2.split('|');

    // Must have same base operation
    if (parts1[0] !== parts2[0]) return false;

    // For product requests, check if same category/type with different pagination/sorting
    if (parts1[0].includes('products')) {
      const params1 = this.parseKeyParams(parts1.slice(1));
      const params2 = this.parseKeyParams(parts2.slice(1));

      // Same category but different page/sort can be considered similar
      if (params1.categoryid && params2.categoryid && params1.categoryid === params2.categoryid) {
        // Only consider similar if page difference is small (within 2 pages)
        const page1 = parseInt(params1.page) || 0;
        const page2 = parseInt(params2.page) || 0;
        return Math.abs(page1 - page2) <= 1;
      }

      // Same search query with different pagination
      if (params1.query && params2.query && params1.query === params2.query) {
        const page1 = parseInt(params1.page) || 0;
        const page2 = parseInt(params2.page) || 0;
        return Math.abs(page1 - page2) <= 1;
      }
    }

    return false;
  }

  /**
   * Parse parameters from cache key parts
   * @param {Array} keyParts - Array of key parts
   * @returns {Object} Parsed parameters
   */
  parseKeyParams(keyParts) {
    const params = {};
    keyParts.forEach(part => {
      const [key, value] = part.split(':');
      if (key && value !== undefined) {
        params[key.toLowerCase()] = value;
      }
    });
    return params;
  }

  /**
   * Execute a request with enhanced deduplication
   * @param {string} key - Unique identifier for the request
   * @param {Function} requestFn - Function that returns a Promise for the API call
   * @param {Object} options - Options for caching and deduplication
   * @returns {Promise} The API result
   */
  async request(key, requestFn, options = {}) {
    const {
      ttl = this.defaultTTL,
      forceRefresh = false,
      params = {},
      enableSimilarityCheck = true
    } = options;

    // Generate normalized cache key
    const cacheKey = typeof key === 'string' && key.includes('|')
      ? key // Already a cache key
      : this.generateCacheKey(key, params);

    // Check cache first (unless force refresh)
    if (!forceRefresh && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey);
      const age = Date.now() - cached.timestamp;

      if (age < ttl) {
        this.stats.hits++;
        return cached.data;
      } else {
        // Cache expired, remove it
        this.cache.delete(cacheKey);
      }
    }

    // Check for similar or duplicate pending requests
    if (enableSimilarityCheck) {
      const similarRequest = this.findSimilarPendingRequest(cacheKey);
      if (similarRequest) {
        return similarRequest;
      }
    } else if (this.pendingRequests.has(cacheKey)) {
      this.stats.duplicatesPrevented++;
      return this.pendingRequests.get(cacheKey);
    }

    // Record cache miss
    this.stats.misses++;


    // Execute the request


    const startTime = Date.now();
    const promise = requestFn()
      .then(result => {
        const duration = Date.now() - startTime;


        // Cache the result
        this.cache.set(cacheKey, {
          data: result,
          timestamp: Date.now(),
          duration
        });

        // Track request history for analytics
        this.updateRequestHistory(cacheKey, duration, true);

        return result;
      })
      .catch(error => {
        const duration = Date.now() - startTime;
        console.error(`❌ Request failed: ${cacheKey} (${duration}ms)`, error);

        // Track failed request
        this.updateRequestHistory(cacheKey, duration, false);

        throw error;
      })
      .finally(() => {
        // Remove from pending requests
        this.pendingRequests.delete(cacheKey);

      });

    // Store pending request
    this.pendingRequests.set(cacheKey, promise);


    return promise;
  }

  /**
   * Update request history for analytics
   * @param {string} cacheKey - Request cache key
   * @param {number} duration - Request duration in ms
   * @param {boolean} success - Whether request succeeded
   */
  updateRequestHistory(cacheKey, duration, success) {
    const baseKey = cacheKey.split('|')[0];

    if (!this.requestHistory.has(baseKey)) {
      this.requestHistory.set(baseKey, {
        count: 0,
        totalDuration: 0,
        successCount: 0,
        failureCount: 0,
        averageDuration: 0,
        lastRequest: Date.now()
      });
    }

    const history = this.requestHistory.get(baseKey);
    history.count++;
    history.totalDuration += duration;
    history.averageDuration = history.totalDuration / history.count;
    history.lastRequest = Date.now();

    if (success) {
      history.successCount++;
    } else {
      history.failureCount++;
    }
  }

  /**
   * Clear cache for a specific key or pattern
   * @param {string|RegExp} keyOrPattern - Key or pattern to clear
   */
  clearCache(keyOrPattern = null) {
    if (keyOrPattern === null) {
      const size = this.cache.size;
      this.cache.clear();
      return size;
    }

    let clearedCount = 0;

    if (typeof keyOrPattern === 'string') {
      // Clear specific key or keys containing pattern
      const keysToDelete = [];

      for (const [key] of this.cache) {
        if (key === keyOrPattern || key.includes(keyOrPattern)) {
          keysToDelete.push(key);
        }
      }

      keysToDelete.forEach(key => {
        this.cache.delete(key);
        clearedCount++;
      });

    } else if (keyOrPattern instanceof RegExp) {
      // Clear keys matching regex
      const keysToDelete = [];

      for (const [key] of this.cache) {
        if (keyOrPattern.test(key)) {
          keysToDelete.push(key);
        }
      }

      keysToDelete.forEach(key => {
        this.cache.delete(key);
        clearedCount++;
      });

    }

    return clearedCount;
  }

  /**
   * Check if a request is currently pending
   * @param {string} key - Request key to check
   * @returns {boolean} True if request is pending
   */
  isPending(key) {
    const cacheKey = typeof key === 'string' && key.includes('|') ? key : this.generateCacheKey(key);
    return this.pendingRequests.has(cacheKey);
  }

  /**
   * Check if a result is cached and not expired
   * @param {string} key - Request key to check
   * @param {number} ttl - Time-to-live in milliseconds
   * @returns {boolean} True if cached and not expired
   */
  isCached(key, ttl = this.defaultTTL) {
    const cacheKey = typeof key === 'string' && key.includes('|') ? key : this.generateCacheKey(key);

    if (!this.cache.has(cacheKey)) return false;

    const cached = this.cache.get(cacheKey);
    const age = Date.now() - cached.timestamp;
    return age < ttl;
  }

  /**
   * Get cached data without executing request
   * @param {string} key - Request key
   * @returns {any|null} Cached data or null
   */
  getCached(key) {
    const cacheKey = typeof key === 'string' && key.includes('|') ? key : this.generateCacheKey(key);

    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey).data;
    }

    return null;
  }

  /**
   * Warmup cache by prefetching common requests
   * @param {Array} requests - Array of {key, requestFn, options} objects
   * @returns {Promise} Promise that resolves when warmup is complete
   */
  async warmupCache(requests = []) {

    const warmupPromises = requests.map(async ({ key, requestFn, options = {} }) => {
      try {
        await this.request(key, requestFn, { ...options, enableSimilarityCheck: false });
      } catch (error) {
        console.warn(`⚠️ Warmup failed for ${key}:`, error.message);
      }
    });

    await Promise.allSettled(warmupPromises);
  }

  /**
   * Get comprehensive cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    const totalRequests = this.stats.hits + this.stats.misses;
    const hitRate = totalRequests > 0 ? ((this.stats.hits / totalRequests) * 100).toFixed(2) : 0;

    // Calculate average cache age
    let totalAge = 0;
    let cacheCount = 0;
    const now = Date.now();

    for (const [, cached] of this.cache) {
      totalAge += (now - cached.timestamp);
      cacheCount++;
    }

    const averageCacheAge = cacheCount > 0 ? (totalAge / cacheCount) : 0;

    return {
      cache: {
        size: this.cache.size,
        keys: Array.from(this.cache.keys()),
        averageAge: `${(averageCacheAge / 1000).toFixed(1)}s`
      },
      pending: {
        size: this.pendingRequests.size,
        keys: Array.from(this.pendingRequests.keys())
      },
      performance: {
        hits: this.stats.hits,
        misses: this.stats.misses,
        hitRate: `${hitRate}%`,
        duplicatesPrevented: this.stats.duplicatesPrevented,
        similarRequestsPrevented: this.stats.similarRequestsPrevented
      },
      history: Object.fromEntries(this.requestHistory.entries())
    };
  }

  /**
   * Get detailed performance report
   * @returns {Object} Performance report
   */
  getPerformanceReport() {
    const stats = this.getStats();
    const report = {
      summary: {
        totalRequests: this.stats.hits + this.stats.misses,
        cacheEfficiency: stats.performance.hitRate,
        duplicatesPreventionRate: this.stats.duplicatesPrevented + this.stats.similarRequestsPrevented,
        currentCacheSize: this.cache.size,
        pendingRequests: this.pendingRequests.size
      },
      topEndpoints: [],
      slowestEndpoints: [],
      recommendations: []
    };

    // Analyze request history
    const historyEntries = Array.from(this.requestHistory.entries())
      .map(([endpoint, data]) => ({
        endpoint,
        ...data,
        successRate: ((data.successCount / data.count) * 100).toFixed(2)
      }))
      .sort((a, b) => b.count - a.count);

    report.topEndpoints = historyEntries.slice(0, 5);
    report.slowestEndpoints = historyEntries
      .sort((a, b) => b.averageDuration - a.averageDuration)
      .slice(0, 3);

    // Generate recommendations
    if (parseFloat(stats.performance.hitRate) < 50) {
      report.recommendations.push('Consider increasing cache TTL for better hit rates');
    }

    if (this.stats.duplicatesPrevented > 10) {
      report.recommendations.push('High duplicate request prevention - good deduplication working');
    }

    const slowEndpoints = report.slowestEndpoints.filter(e => e.averageDuration > 1000);
    if (slowEndpoints.length > 0) {
      report.recommendations.push(`Consider optimizing slow endpoints: ${slowEndpoints.map(e => e.endpoint).join(', ')}`);
    }

    return report;
  }

  /**
   * Reset all statistics and cache
   */
  reset() {
    this.cache.clear();
    this.pendingRequests.clear();
    this.requestHistory.clear();
    this.stats = {
      hits: 0,
      misses: 0,
      duplicatesPrevented: 0,
      similarRequestsPrevented: 0
    };
  }

  /**
   * Cleanup expired cache entries
   * @param {number} maxAge - Maximum age in milliseconds (default: 5 minutes)
   */
  cleanup(maxAge = 5 * 60 * 1000) {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [key, cached] of this.cache) {
      if (now - cached.timestamp > maxAge) {
        this.cache.delete(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
    }

    return cleanedCount;
  }
}

// Create singleton instance
const enhancedRequestDeduplicator = new EnhancedRequestDeduplicator();

// Start periodic cleanup
setInterval(() => {
  enhancedRequestDeduplicator.cleanup();
}, 5 * 60 * 1000); // Every 5 minutes

/**
 * Helper function for common API patterns with enhanced features
 */
export const dedupedRequest = (key, requestFn, options = {}) => {
  return enhancedRequestDeduplicator.request(key, requestFn, options);
};

/**
 * Helper for product requests with smart parameter handling
 */
export const dedupedProductRequest = (params, requestFn, options = {}) => {
  const baseKey = 'products';
  return enhancedRequestDeduplicator.request(baseKey, requestFn, {
    ...options,
    params,
    ttl: 30000 // 30 seconds for product data
  });
};

/**
 * Helper for category requests (longer cache)
 */
export const dedupedCategoryRequest = (params, requestFn, options = {}) => {
  const baseKey = 'categories';
  return enhancedRequestDeduplicator.request(baseKey, requestFn, {
    ...options,
    params,
    ttl: 300000 // 5 minutes for category data
  });
};

/**
 * Helper for search requests (shorter cache)
 */
export const dedupedSearchRequest = (params, requestFn, options = {}) => {
  const baseKey = 'search';
  return enhancedRequestDeduplicator.request(baseKey, requestFn, {
    ...options,
    params,
    ttl: 60000 // 1 minute for search results
  });
};

/**
 * Helper for requests that should be cached longer (like configuration)
 */
export const dedupedConfigRequest = (key, requestFn) => {
  return enhancedRequestDeduplicator.request(key, requestFn, { ttl: 300000 }); // 5 minutes
};

/**
 * Helper for requests that should be cached shorter (like real-time data)
 */
export const dedupedRealtimeRequest = (key, requestFn) => {
  return enhancedRequestDeduplicator.request(key, requestFn, { ttl: 10000 }); // 10 seconds
};

/**
 * Clear cache for specific keys (useful when data changes)
 */
export const clearRequestCache = (keyOrPattern) => {
  return enhancedRequestDeduplicator.clearCache(keyOrPattern);
};

/**
 * Force refresh a request (ignores cache)
 */
export const forceRefreshRequest = (key, requestFn, options = {}) => {
  return enhancedRequestDeduplicator.request(key, requestFn, { ...options, forceRefresh: true });
};

/**
 * Warmup cache with common requests
 */
export const warmupRequestCache = (requests) => {
  return enhancedRequestDeduplicator.warmupCache(requests);
};

/**
 * Get cache statistics
 */
export const getRequestCacheStats = () => {
  return enhancedRequestDeduplicator.getStats();
};

/**
 * Get performance report
 */
export const getRequestPerformanceReport = () => {
  return enhancedRequestDeduplicator.getPerformanceReport();
};

// Add debugging utilities to browser console
if (typeof window !== 'undefined') {
  window.debugRequestCache = {
    stats: () => enhancedRequestDeduplicator.getStats(),
    report: () => enhancedRequestDeduplicator.getPerformanceReport(),
    clear: (pattern) => enhancedRequestDeduplicator.clearCache(pattern),
    reset: () => enhancedRequestDeduplicator.reset(),
    cleanup: () => enhancedRequestDeduplicator.cleanup(),
    isPending: (key) => enhancedRequestDeduplicator.isPending(key),
    isCached: (key, ttl) => enhancedRequestDeduplicator.isCached(key, ttl),
    getCached: (key) => enhancedRequestDeduplicator.getCached(key),
    warmup: (requests) => enhancedRequestDeduplicator.warmupCache(requests)
  };
}

export default enhancedRequestDeduplicator;